<template>
  <Layout column border>
    <Form ref="editorForm" :values="computedData" :show-header="false">
      <FormSection :show-header="false">
        <FormField name="label" label="Status Name">
          <TextField required @update:value="onLabelValueUpdate" />
        </FormField>
        <FormField name="description" label="Status Description">
          <TextField @update:value="onDescriptionValueUpdate" />
        </FormField>
        <FormField name="baseState" label="Base State Mapping" :span="2">
          <Select required :items="baseStates" @update:value="onBaseStateValueUpdate" />
        </FormField>
        <FormField name="isActive" label="Is Active" :span="1">
          <Checkbox :checked="node.data.isActive" @update:value="onIsActiveValueUpdate">
            Is Active
          </Checkbox>
        </FormField>
        <FormField label="Workflow Condition" :span="2" class="workflow-condition">
          <ConditionEditor
            ref="workflowConditionEditor"
            target-field-name="workflowConditionText"
            :condition-text="computedData.workflowConditionText"
            :is-valid-xml="isValidXml"
            @update:condition-text="onConditionValueUpdate"
            :component-id="componentId"
          ></ConditionEditor>
        </FormField>
        <FormField name="visibilityOption" label="Visibility" :span="2" class="visibility-selector">
          <RadioGroup
            required
            :items="[
              { data: { value: 'always', label: 'Always Show' } },
              { data: { value: 'conditionally', label: 'Conditionally Show' } },
            ]"
            @update:value="onVisibilityOptionUpdate"
          >
          </RadioGroup>
          <FormField
            label="Visibility Condition"
            class="visibility-condition"
            v-if="computedData.visibilityOption === 'conditionally'"
          >
            <ConditionEditor
              ref="visibilityConditionEditor"
              target-field-name="visibilityConditionText"
              :condition-text="computedData.visibilityConditionText"
              :is-valid-xml="isValidXml"
              @update:condition-text="onConditionValueUpdate"
              :component-id="componentId"
            ></ConditionEditor>
          </FormField>
        </FormField>
      </FormSection>
      <div class="action-bar-footer">
        <Button
          :outlined="true"
          append-icon="bwise-full/trash"
          class="inline-flex"
          @click="onRemoveStateClick"
          v-tooltip.top="'Remove'"
        ></Button>
      </div>
    </Form>
  </Layout>
</template>

<script>
import Layout from '@wizer/core/components/layout/Layout'
import Form from '@wizer/core/components/form/Form'
import FormSection from '@wizer/core/components/form/FormSection'
import FormField from '@wizer/core/components/form/FormField'
import TextField from '@wizer/core/components/text-field/TextField'
import Select from '@wizer/core/components/select/Select'
import Checkbox from '@wizer/core/components/checkbox/Checkbox'
import RadioGroup from '@wizer/core/components/radio-group/RadioGroup'
import _ from 'lodash'
import Button from '@wizer/core/components/button/Button'
import { mapState } from 'vuex'
import IframeMixin from '@/mixins/IframeMixin'
import FormStateService from '@/services/FormStateService'
import UniqueId from '@/utils/UniqueIdHelper'
import ConditionEditor from '@/components/designer/ConditionEditor'

export default {
  name: 'StateEditor',
  mixins: [IframeMixin],
  components: {
    ConditionEditor,
    Button,
    Layout,
    Form,
    FormSection,
    FormField,
    TextField,
    Select,
    Checkbox,
    RadioGroup,
  },
  props: {
    node: Object,
    baseStates: Array,
    selectedStateIndex: Number,
    componentId: String,
    isValidXml: Function,
  },
  computed: {
    computedData() {
      return _.clone(this.node.data)
    },
    ...mapState({
      notificationDuration: (state) => state.Notification.notificationDuration,
    }),
  },
  async mounted() {
    await this.revalidateEditorForm()
    await this.fetchConditionJSONS()
  },
  methods: {
    // Due to the way Forms are rendered validation call will only take effect
    // on a second or later recalculations
    async revalidateEditorForm() {
      await this.$nextTick(() => {})
      await this.$nextTick(() => {
        this.$refs.editorForm.validate()
      })
    },
    // Note we probably need a more efficient way to update node values
    onLabelValueUpdate(newVal) {
      this.node.update((data) => {
        data.label = newVal
      })
    },
    onDescriptionValueUpdate(newVal) {
      this.node.update((data) => {
        data.description = newVal
      })
    },
    onConditionValueUpdate(data) {
      this.node.update((nodeData) => {
        nodeData[data.targetFieldName] = data.condition
      })
    },
    onBaseStateValueUpdate(newVal) {
      this.node.update((data) => {
        data.baseState = newVal
      })
    },
    onIsActiveValueUpdate(newVal) {
      this.node.update((data) => {
        data.isActive = newVal
      })
    },
    onVisibilityOptionUpdate(newVal) {
      this.node.update((data) => {
        data.visibilityOption = newVal
      })
    },
    async onRemoveStateClick() {
      let notification
      let responseData
      let hasStateInRecord = false
      const duration = this.notificationDuration
      try {
        const data = {
          formId: this.$route.params.id,
          stateId: this.node.id,
        }
        if (!UniqueId.isANewRecord(this.node.id)) {
          responseData = await FormStateService.isStateUsedInRecords(data)
          hasStateInRecord = responseData.data.hasStateInRecord
        }
        if (hasStateInRecord) {
          notification = {
            type: 'warning',
            message: "This Status can't be removed as records are already using it.",
            duration,
          }
        } else {
          this.$emit('removeState')
        }
      } catch (error) {
        const message = error.response ? error.response.data : error
        notification = { type: 'error', message, duration }
      } finally {
        await this.$store.dispatch('Notification/add', notification, { root: true })
      }
    },
    async fetchConditionJSONS() {
      await this.$refs.workflowConditionEditor.getLatestConditionJSON()
      if (this.$refs.visibilityConditionEditor) {
        await this.$refs.visibilityConditionEditor.getLatestConditionJSON()
      }
    },
  },
  watch: {
    // This causes editor form to be refreshed each time a drag action ends
    // eslint-disable-next-line func-names
    '$store.state.FormStatus.editorRefreshNeeded': async function () {
      await this.revalidateEditorForm()
      await this.fetchConditionJSONS()
    },
    // We want to revalidate everytime selectedStateIndex changes as the newly added data may
    // contain validation errors
    async selectedStateIndex() {
      await this.revalidateEditorForm()
      await this.fetchConditionJSONS()
    },
  },
}
</script>

<style>
.condition-button {
  margin-top: 0.25rem;
}
.w-code-editor div.CodeMirror {
  @apply bg-white;
}
.w-button__append-icon {
  margin: 0;
}
.condition-placeholder {
  @apply p-2;
  color: #f56565;
}
.action-bar-footer {
  @apply flex-1 text-right;
}
.visibility-selector,
.visibility-condition,
.workflow-condition {
  @apply w-full !important;
}
</style>
